import { useState } from "react";

// Preview cards for the web sources of an attachment answer, the same card the FAQ page draws (FaqChatbot SourceCard):
// the page's image, its domain, title and description, and a hint when the site would not give a preview. The data is
// the FAQ service's own (Root passes domain/image/description/enrichmentStatus through); a source without any of it
// is drawn as the plain link it always was. The FAQ page shows the cited sources first and folds the rest; an
// attachment answer cites no single web source, so the first few are shown and the rest fold away.
export const VISIBLE_CARDS = 3;

const HINT = {
  blocked: {
    en: "Source blocks automated preview",
    zh: "此來源封鎖了自動預覽",
  },
  not_attempted: { en: "Preview not fetched", zh: "未擷取預覽" },
};

export const hasPreview = (source) => !!(source.image || source.description);

function SourceCard({ source, index, en }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = source.image && !imageFailed;
  const hint =
    source.enrichmentStatus && source.enrichmentStatus !== "ok"
      ? HINT[source.enrichmentStatus]?.[en ? "en" : "zh"]
      : null;
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      data-testid="web-source-card"
      className="flex w-48 shrink-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow hover:shadow-md dark:border-gray-600 dark:bg-gray-800"
    >
      {showImage && (
        <div className="flex h-24 items-center justify-center overflow-hidden bg-slate-100 dark:bg-gray-700">
          <img
            src={source.image}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        </div>
      )}
      <div className="flex flex-col gap-0.5 p-2">
        <span className="text-xs text-slate-400 dark:text-gray-500">
          [{index + 1}] {source.domain || ""}
        </span>
        <span className="line-clamp-2 text-sm font-medium text-slate-800 dark:text-gray-100">
          {source.title || source.domain || source.url}
        </span>
        {source.description && (
          <span className="line-clamp-2 text-xs text-slate-500 dark:text-gray-400">
            {source.description}
          </span>
        )}
        {hint && (
          <span className="line-clamp-1 text-xs italic text-slate-400 dark:text-gray-500">
            {hint}
          </span>
        )}
      </div>
    </a>
  );
}

export default function WebSourceCards({ sources, language }) {
  const en = language === "en";
  const [open, setOpen] = useState(false);
  const shown = open ? sources : sources.slice(0, VISIBLE_CARDS);
  const folded = sources.length - VISIBLE_CARDS;
  return (
    <div className="w-full">
      <div className="flex flex-row flex-wrap gap-2">
        {shown.map((source, index) => (
          <SourceCard
            key={source.id || source.url || index}
            source={source}
            index={index}
            en={en}
          />
        ))}
      </div>
      {folded > 0 && (
        <button
          type="button"
          className="mt-2 text-xs text-orange-700 underline dark:text-orange-300"
          onClick={() => setOpen((value) => !value)}
        >
          {open
            ? en
              ? "Show fewer sources"
              : "收合來源"
            : en
              ? `Show ${folded} more source${folded > 1 ? "s" : ""}`
              : `顯示其餘 ${folded} 個來源`}
        </button>
      )}
    </div>
  );
}
